import React, { useState, useEffect } from 'react';
import { Box, Modal, Typography, TextField, Button, Select, MenuItem, InputLabel, FormControl } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string, surname: string, company: string, email: string, companyId: string, language: string) => void;
  companyId: string;
}

const ModalCreateUser: React.FC<Props> = ({ open, onClose, onCreate, companyId }) => {
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState('EN');
  const { t, i18n } = useTranslation();
  
  useEffect(() => {
    const storedLang = localStorage.getItem('language') || 'EN';
    i18n.changeLanguage(storedLang);
  }, []);

  const [errors, setErrors] = useState({
    name: false,
    surname: false,
    company: false,
    email: false,
    emailFormat: false,
  });

  const handleCreate = () => {
    const hasErrors = {
      name: name.trim() === '',
      surname: surname.trim() === '',
      company: company.trim() === '',
      email: email.trim() === '',
      emailFormat: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
    };

    setErrors(hasErrors);

    const isValid =
      !hasErrors.name &&
      !hasErrors.surname &&
      !hasErrors.company &&
      !hasErrors.email &&
      !hasErrors.emailFormat;

    if (!isValid) return;

    onCreate(name, surname, company, email, companyId, language);
    onClose();
    setName('');
    setSurname('');
    setCompany('');
    setEmail('');
    setLanguage('EN');
    setErrors({
      name: false,
      surname: false,
      company: false,
      email: false,
      emailFormat: false,
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400, bgcolor: 'background.paper',
        p: 3, borderRadius: 2, boxShadow: 24,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <PersonIcon sx={{ marginRight: 1, color: '#73AF73', mb:3 }} />
          <Typography variant="h6" sx={{ color: '#73AF73', mb:3  }}>{t('createNewUser')}</Typography>
        </Box>
        <TextField
          fullWidth
          label={t('name')}
          value={name}
          onChange={e => setName(e.target.value)}
          sx={{
              marginBottom: 2,
              '& label.Mui-focused': {
                color: '#A5D6A7',
              },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#A5D6A7',
                },
              },
          }}
          error={errors.name}
          helperText={errors.name ? t('nameRequired') : ''}
        />
        <TextField
          fullWidth
          label={t('surname')}
          value={surname}
          onChange={e => setSurname(e.target.value)}
          sx={{
              marginBottom: 2,
              '& label.Mui-focused': {
                color: '#A5D6A7',
              },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#A5D6A7',
                },
              },
          }}
          error={errors.surname}
          helperText={errors.surname ? t('surnameRequired') : ''}
        />
        <TextField
          fullWidth
          label={t('company')}
          value={company}
          onChange={e => setCompany(e.target.value)}
          sx={{
              marginBottom: 2,
              '& label.Mui-focused': {
                color: '#A5D6A7',
              },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#A5D6A7',
                },
              },
          }}
          error={errors.company}
          helperText={errors.company ? t('companyRequired') : ''}
        />
        <TextField
          fullWidth
          label={t('email')}
          value={email}
          onChange={e => setEmail(e.target.value)}
          sx={{
              marginBottom: 2,
              '& label.Mui-focused': {
                color: '#A5D6A7',
              },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#A5D6A7',
                },
              },
          }}
          error={errors.email || errors.emailFormat}
          helperText={
            errors.email
              ? 'Email is required'
              : errors.emailFormat
              ? 'Invalid email format'
              : ''
          }
        />
        <FormControl
          fullWidth
          sx={{
            mb: 2,
            '& label.Mui-focused': {
              color: '#A5D6A7',
            },
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: '#A5D6A7',
              },
            },
          }}
        >
          <InputLabel>{t('language')}</InputLabel>
          <Select
            value={language}
            label="Language"
            onChange={(e) => setLanguage(e.target.value)}
          >
            <MenuItem value="EN">English</MenuItem>
            <MenuItem value="DE">Deutsch</MenuItem>
            <MenuItem value="FR">Français</MenuItem>
            <MenuItem value="IT">Italiano</MenuItem>
            <MenuItem value="ES">Español</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              color: '#d32f2f',
              borderColor: '#d32f2f',
              '&:hover': {
                backgroundColor: '#fddede', 
                borderColor: '#d32f2f',
              },
            }}
          >
            {t('cancel')}
          </Button>
          <Button variant="contained" onClick={handleCreate} sx={{ backgroundColor: '#73AF73' }}>{t('create')}</Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalCreateUser;